// Clinic-time date helpers (Frontend §8.1 "ui/time/*"; D-21): patients and staff see clinic time whatever the
// browser's time zone. Clinic-local dates are 'YYYY-MM-DD' strings (API.md §1.4) and are done with UTC arithmetic,
// so no browser time zone ever leaks in; instants are formatted with Intl in the clinic's zone.

export type LocalDate = string

const pad = (n: number) => String(n).padStart(2, '0')
const parse = (d: LocalDate) => {
  const [y = 0, m = 1, day = 1] = d.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, day))
}
const key = (d: Date): LocalDate => `${String(d.getUTCFullYear())}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`

export const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

/** `date` plus `n` calendar days. */
export function addDays(date: LocalDate, n: number): LocalDate {
  const d = parse(date)
  d.setUTCDate(d.getUTCDate() + n)
  return key(d)
}

/** 0 = Sunday. */
export const weekdayOf = (date: LocalDate): number => parse(date).getUTCDay()

/** The Sunday that starts the week holding `date` (patient weeks start on Sunday, UserFlows P-08). */
export const sundayOf = (date: LocalDate): LocalDate => addDays(date, -weekdayOf(date))

/** The 7 dates Sunday–Saturday. */
export const weekDates = (weekStart: LocalDate): LocalDate[] =>
  Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const

const monthIndex = (d: LocalDate) => parse(d).getUTCMonth()
const dayOfMonth = (d: LocalDate) => parse(d).getUTCDate()

/** "Oct 4 – 10", "Oct 25 – 31", "Nov 29 – Dec 5" (UserFlows P-08 week label). */
export function weekLabel(weekStart: LocalDate): string {
  const end = addDays(weekStart, 6)
  const start = `${MONTHS_SHORT[monthIndex(weekStart)] ?? ''} ${String(dayOfMonth(weekStart))}`
  const last =
    monthIndex(end) === monthIndex(weekStart)
      ? String(dayOfMonth(end))
      : `${MONTHS_SHORT[monthIndex(end)] ?? ''} ${String(dayOfMonth(end))}`
  return `${start} – ${last}`
}

/** "October". */
export const monthName = (date: LocalDate): string => MONTHS[monthIndex(date)] ?? ''

/** The first day of the month after `date`'s. */
export function nextMonthStart(date: LocalDate): LocalDate {
  const d = parse(date)
  return key(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)))
}

export interface MonthTab {
  label: string
  /** The week to show when the tab is picked. */
  weekStart: LocalDate
  disabled: boolean
}

/**
 * P-08 month tabs: this month and next month (UserFlows P-08). Next month is disabled when it starts after the
 * booking window's last day. "This month" opens on the window's first week.
 */
export function monthTabs(window: { firstDate: LocalDate; lastDate: LocalDate }): [MonthTab, MonthTab] {
  const next = nextMonthStart(window.firstDate)
  return [
    { label: monthName(window.firstDate), weekStart: sundayOf(window.firstDate), disabled: false },
    { label: monthName(next), weekStart: sundayOf(next), disabled: next > window.lastDate },
  ]
}

/** Which tab a week belongs to: the month of its first bookable day (0 = this month, 1 = next). */
export function monthTabOf(weekStart: LocalDate, window: { firstDate: LocalDate; lastDate: LocalDate }): 0 | 1 {
  const first = weekDates(weekStart).find((d) => d >= window.firstDate && d <= window.lastDate) ?? weekStart
  return first >= nextMonthStart(window.firstDate) ? 1 : 0
}

/** "Wednesday, October 14" for a clinic-local date. */
export function fmtLongDate(date: LocalDate): string {
  return `${WEEKDAYS[weekdayOf(date)] ?? ''}, ${monthName(date)} ${String(dayOfMonth(date))}`
}

/** Short date "Oct 14". */
export const fmtShortDate = (date: LocalDate): string =>
  `${MONTHS_SHORT[monthIndex(date)] ?? ''} ${String(dayOfMonth(date))}`

const formatters = new Map<string, Intl.DateTimeFormat>()
function formatter(tz: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const k = `${tz}|${JSON.stringify(opts)}`
  let f = formatters.get(k)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts })
    formatters.set(k, f)
  }
  return f
}

/** The clinic-local date and time of day of an instant. */
export function zonedParts(iso: string | number | Date, tz: string): { date: LocalDate; hour: number; minute: number } {
  const parts = formatter(tz, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso))
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '0'
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
  }
}

/** The clinic's "today". */
export const todayIn = (tz: string, now: number | Date = Date.now()): LocalDate => zonedParts(now, tz).date

/** "9:00 AM" in clinic time. */
export function fmtTime(iso: string | number | Date, tz: string): string {
  return formatter(tz, { hour: 'numeric', minute: '2-digit' }).format(new Date(iso))
}

/** "Wednesday, October 14" for an instant, in clinic time. */
export const fmtLongDateOf = (iso: string | number | Date, tz: string): string => fmtLongDate(zonedParts(iso, tz).date)

export type DayPart = 'Morning' | 'Afternoon' | 'Evening'

/** P-08 time groups: before 12:00 Morning, 12:00–16:59 Afternoon, from 17:00 Evening (clinic time). */
export function dayPartOf(iso: string, tz: string): DayPart {
  const { hour } = zonedParts(iso, tz)
  return hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening'
}

/** P-12 "Arrive by": 15 minutes before the start, in clinic time (9:00 → 8:45, 12:10 → 11:55). */
export function arriveByTime(startIso: string, tz: string): string {
  return fmtTime(new Date(startIso).getTime() - 15 * 60_000, tz)
}

/** P-09 countdown `m:ss` from whole seconds (never negative). */
export function fmtCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  return `${String(Math.floor(s / 60))}:${pad(s % 60)}`
}

/** Whole calendar days from `from` to `to` (clinic-local dates). */
export const daysBetween = (from: LocalDate, to: LocalDate): number =>
  Math.round((parse(to).getTime() - parse(from).getTime()) / 86_400_000)

/** C-01 day headers: "Today", "Tomorrow", "Yesterday", "In N days" or "N days ago". */
export function relativeDay(date: LocalDate, today: LocalDate): string {
  const n = daysBetween(today, date)
  if (n === 0) return 'Today'
  if (n === 1) return 'Tomorrow'
  if (n === -1) return 'Yesterday'
  return n > 0 ? `In ${String(n)} days` : `${String(-n)} days ago`
}

/** "3 hr ago" (C-01 Booked column, the mockup's `relTime`). */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const diff = now - new Date(iso).getTime()
  const future = diff < 0
  const m = Math.round(Math.abs(diff) / 60_000)
  if (m < 1) return future ? 'in a moment' : 'just now'
  let s: string
  if (m < 60) s = `${String(m)} min`
  else if (m < 60 * 24) s = `${String(Math.round(m / 60))} hr`
  else {
    const d = Math.round(m / 1440)
    s = `${String(d)} day${d === 1 ? '' : 's'}`
  }
  return future ? `in ${s}` : `${s} ago`
}

/**
 * The instant at clinic-local midnight of `date` (C-01 presets → API `from` / `to`, API.md §1.4). Converges on the
 * zone's offset at that instant, so a DST-change day (US changes happen at 02:00) still gets its own midnight.
 */
export function zonedMidnight(date: LocalDate, tz: string): Date {
  const target = Date.parse(`${date}T00:00:00Z`)
  let t = target
  for (let i = 0; i < 3; i++) {
    const p = zonedParts(t, tz)
    const local = Date.parse(`${p.date}T${pad(p.hour)}:${pad(p.minute)}:00Z`)
    if (local === target) break
    t -= local - target
  }
  return new Date(t)
}

/** "Oct 8, 9:02 AM" for an instant, in clinic time (C-01 Booked column, mockup `fmtDateTime`). */
export function fmtDateTimeOf(iso: string, tz: string): string {
  return `${fmtShortDate(zonedParts(iso, tz).date)}, ${fmtTime(iso, tz)}`
}

/** "Thu, Oct 15" for an instant, in clinic time (C-01 Appointment column). */
export function fmtDayOf(iso: string, tz: string): string {
  const date = zonedParts(iso, tz).date
  return `${WEEKDAYS_SHORT[weekdayOf(date)] ?? ''}, ${fmtShortDate(date)}`
}

/** The first day of `date`'s month. */
export const monthStart = (date: LocalDate): LocalDate => `${date.slice(0, 7)}-01`

/** The first day of the month before `date`'s. */
export function prevMonthStart(date: LocalDate): LocalDate {
  const d = parse(date)
  return key(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1)))
}

// ---- Date-range presets (C-01 filters; Frontend §6.1 "Filter search params"): kept as ids in the URL, resolved in
// the clinic's time zone when the query runs, so a shared or bookmarked link stays relative.

export interface DatePreset {
  id: string
  label: string
}

/** C-01 booked view (no future dates): Any time, Today, Last 7 days, Last 30 days, This month, Last month. */
export const BOOKED_PRESETS: readonly DatePreset[] = [
  { id: 'any', label: 'Any time' },
  { id: 'today', label: 'Today' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'month', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
]

/** C-01 appointment view: Any date, Today, Tomorrow, Next 7 / 30 days, Last 7 / 30 days. */
export const APPOINTMENT_PRESETS: readonly DatePreset[] = [
  { id: 'any', label: 'Any date' },
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'next7', label: 'Next 7 days' },
  { id: 'next30', label: 'Next 30 days' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
]

/** A clinic-local date range: `from` inclusive, `to` exclusive. */
export interface LocalRange {
  from: LocalDate
  to: LocalDate
}

/** The clinic-local dates of a preset relative to the clinic's `today`; null for "any" or an unknown id. */
export function presetRange(id: string, today: LocalDate): LocalRange | null {
  const day = (n: number) => addDays(today, n)
  switch (id) {
    case 'today':
      return { from: today, to: day(1) }
    case 'tomorrow':
      return { from: day(1), to: day(2) }
    case 'last7':
      return { from: day(-6), to: day(1) }
    case 'last30':
      return { from: day(-29), to: day(1) }
    case 'next7':
      return { from: today, to: day(7) }
    case 'next30':
      return { from: today, to: day(30) }
    case 'month':
      return { from: monthStart(today), to: day(1) }
    case 'lastMonth':
      return { from: prevMonthStart(today), to: monthStart(today) }
    default:
      return null
  }
}

/** API instants for a clinic-local range (API.md §1.4: `from` inclusive, `to` exclusive), at clinic midnight. */
export function rangeInstants(range: LocalRange, tz: string): { from: string; to: string } {
  return { from: zonedMidnight(range.from, tz).toISOString(), to: zonedMidnight(range.to, tz).toISOString() }
}
